import React, { useState, useEffect, useMemo } from 'react';
import reportingFinancierApi from '../services/reporting-financier';
import useReportingData from '../hooks/useReportingData';
import UpdatingOverlay from './UpdatingOverlay';
import './ReportingPdrApv.css';
import './ReportingVN.css';
import './ReportingStockVO.css';

// Reporting Financier — onglet Stock VO. Source = base snapshot `stock_vo_detail`
// (stockvo.all.csv complet, cf. backend). C'est un état « stock à date » : la
// période (Du/Au) du shell N'EST PAS utilisée ; seuls les filtres concession
// (toolbar partagée) et marque (local) s'appliquent.
//
// Ancienneté = temps en stock depuis l'entrée (champ ENTREE). Stock dormant =
// bucket système n°5 (> 120 jours). Réutilise le design system rpa-* / rvn-*.

const fmtNumber = (v) => v == null ? '—' : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v);
const fmtPct    = (v) => v == null ? '—' : `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(v)} %`;
const fmtMonths = (v) => v == null ? '—' : `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(v)} mois`;
const fmtMoney  = (v) => v == null ? '—' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const fmtDate   = (v) => v ? new Date(v).toLocaleDateString('fr-FR') : '—';
// Libellé mois « YYYY-MM » → « janv. 25 » (colonnes du pivot arrivages).
const MOIS_COURTS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const fmtMois = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return `${MOIS_COURTS[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

// Carte KPI plate (réutilise le style rpa-hero-card).
function KpiHero({ label, value, sublabel, alert }) {
  return (
    <div className={`rpa-hero-card ${alert ? 'svo-hero-alert' : ''}`}>
      <div className="rpa-hero-label">{label}</div>
      <div className="rpa-hero-value">{value}</div>
      <div className="rpa-hero-foot">
        <span className="rpa-hero-sublabel">{sublabel || ' '}</span>
      </div>
    </div>
  );
}

// Tableau de ventilation stock triable (marque / modèle / établissement).
function StockDimTable({ rows, entityLabel, defaultSort = 'nb' }) {
  const [sortKey, setSortKey] = useState(defaultSort);
  const [sortDir, setSortDir] = useState('desc');

  const cols = [
    { key: 'label',          label: entityLabel,    align: 'left',  fmt: (v) => v },
    { key: 'nb',             label: 'En stock',     align: 'right', fmt: fmtNumber },
    { key: 'age_moyen_mois', label: 'Âge moyen',    align: 'right', fmt: fmtMonths },
    { key: 'dormant_nb',     label: 'Dormant >120j', align: 'right', fmt: fmtNumber },
    { key: 'dormant_pct',    label: '% dormant',    align: 'right', fmt: fmtPct },
    { key: 'valeur',         label: 'Valeur stock', align: 'right', fmt: fmtMoney },
  ];

  const sorted = useMemo(() => {
    const arr = [...(rows || [])];
    arr.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const onSort = (key) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'label' ? 'asc' : 'desc'); }
  };

  if (!rows || rows.length === 0) return <div className="rpa-empty">Aucun véhicule en stock.</div>;
  return (
    <div className="rvn-table-wrap">
      <table className="rvn-table">
        <thead>
          <tr>
            {cols.map(c => (
              <th key={c.key}
                  className={`${c.align === 'right' ? 'rvn-num' : ''} rvn-sortable ${sortKey === c.key ? 'rvn-sorted' : ''}`}
                  onClick={() => onSort(c.key)}>
                {c.label}{sortKey === c.key && <span className="rvn-sort-arrow">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={i}>
              {cols.map(c => (
                <td key={c.key} className={c.align === 'right' ? 'rvn-num' : 'rvn-marque-cell'}>{c.fmt(r[c.key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Détail par marque avec drill-down modèles (flèche déroulante par ligne).
function MarqueModeleTable({ rows }) {
  const [sortKey, setSortKey] = useState('nb');
  const [sortDir, setSortDir] = useState('desc');
  const [open, setOpen] = useState({});

  const cols = [
    { key: 'label',          label: 'Marque',        align: 'left',  fmt: (v) => v },
    { key: 'nb',             label: 'En stock',      align: 'right', fmt: fmtNumber },
    { key: 'age_moyen_mois', label: 'Âge moyen',     align: 'right', fmt: fmtMonths },
    { key: 'dormant_nb',     label: 'Dormant >120j', align: 'right', fmt: fmtNumber },
    { key: 'dormant_pct',    label: '% dormant',     align: 'right', fmt: fmtPct },
    { key: 'valeur',         label: 'Valeur stock',  align: 'right', fmt: fmtMoney },
  ];

  const sorted = useMemo(() => {
    const arr = [...(rows || [])];
    arr.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const onSort = (key) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'label' ? 'asc' : 'desc'); }
  };

  if (!rows || rows.length === 0) return <div className="rpa-empty">Aucun véhicule en stock.</div>;
  return (
    <div className="rvn-table-wrap">
      <table className="rvn-table">
        <thead>
          <tr>
            <th style={{ width: 28 }}></th>
            {cols.map(c => (
              <th key={c.key}
                  className={`${c.align === 'right' ? 'rvn-num' : ''} rvn-sortable ${sortKey === c.key ? 'rvn-sorted' : ''}`}
                  onClick={() => onSort(c.key)}>
                {c.label}{sortKey === c.key && <span className="rvn-sort-arrow">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const isOpen = !!open[r.label];
            const hasModeles = (r.modeles || []).length > 0;
            return (
              <React.Fragment key={i}>
                <tr className={`svo-marque-row ${isOpen ? 'svo-marque-open' : ''}`}
                    onClick={() => hasModeles && setOpen(o => ({ ...o, [r.label]: !o[r.label] }))}
                    style={{ cursor: hasModeles ? 'pointer' : 'default' }}>
                  <td className="svo-caret">{hasModeles ? (isOpen ? '▾' : '▸') : ''}</td>
                  <td className="rvn-marque-cell">{r.label} {hasModeles && <span className="svo-nb-modeles">({r.modeles.length})</span>}</td>
                  <td className="rvn-num">{fmtNumber(r.nb)}</td>
                  <td className="rvn-num">{fmtMonths(r.age_moyen_mois)}</td>
                  <td className="rvn-num">{fmtNumber(r.dormant_nb)}</td>
                  <td className="rvn-num">{fmtPct(r.dormant_pct)}</td>
                  <td className="rvn-num">{fmtMoney(r.valeur)}</td>
                </tr>
                {isOpen && (r.modeles || []).map((m, j) => (
                  <tr key={`${i}-${j}`} className="svo-modele-row">
                    <td></td>
                    <td className="svo-modele-label">{m.label}</td>
                    <td className="rvn-num">{fmtNumber(m.nb)}</td>
                    <td className="rvn-num">{fmtMonths(m.age_moyen_mois)}</td>
                    <td className="rvn-num">{fmtNumber(m.dormant_nb)}</td>
                    <td className="rvn-num">{fmtPct(m.dormant_pct)}</td>
                    <td className="rvn-num">{fmtMoney(m.valeur)}</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Ventilation par ancienneté : barres horizontales (les 5 buckets système).
function AncienneteChart({ rows }) {
  if (!rows || rows.length === 0) return <div className="rpa-empty">—</div>;
  const max = Math.max(...rows.map(r => r.nb), 1);
  return (
    <div className="svo-anc">
      {rows.map((r, i) => {
        const isDormant = /120/.test(r.label) && /plus|\+|sup/i.test(r.label);
        return (
          <div className="svo-anc-row" key={i}>
            <span className="svo-anc-label">{r.label}</span>
            <div className="svo-anc-bar-wrap">
              <div className={`svo-anc-bar ${isDormant ? 'svo-anc-bar-alert' : ''}`} style={{ width: `${(r.nb / max) * 100}%` }} />
            </div>
            <span className="svo-anc-val">{fmtNumber(r.nb)} <span className="svo-anc-pct">({fmtPct(r.pct)})</span></span>
          </div>
        );
      })}
    </div>
  );
}

// Pivot des arrivages : lignes = marques (dépliables en modèles), colonnes =
// mois d'entrée en stock (12 derniers + « Avant »), données = nb de châssis.
// Le total de ligne = le stock actuel de la marque (photo à l'instant T).
function ArrivagesPivot({ arrivages }) {
  const [open, setOpen] = useState({});
  if (!arrivages || !(arrivages.rows || []).length)
    return <div className="rpa-empty">Aucun arrivage daté sur la période.</div>;
  const { months, has_before, rows, totaux } = arrivages;
  const cell = (v) => (v ? fmtNumber(v) : <span className="svo-arr-zero">·</span>);

  return (
    <div className="rvn-table-wrap svo-arr-wrap">
      <table className="rvn-table svo-arr-table">
        <thead>
          <tr>
            <th style={{ width: 28 }}></th>
            <th className="svo-arr-marque-th">Marque / modèle</th>
            {has_before && <th className="rvn-num svo-arr-before">Avant</th>}
            {months.map(m => <th key={m} className="rvn-num">{fmtMois(m)}</th>)}
            <th className="rvn-num svo-arr-total">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const isOpen = !!open[r.label];
            const hasModeles = (r.modeles || []).length > 0;
            return (
              <React.Fragment key={i}>
                <tr className={`svo-marque-row ${isOpen ? 'svo-marque-open' : ''}`}
                    onClick={() => hasModeles && setOpen(o => ({ ...o, [r.label]: !o[r.label] }))}
                    style={{ cursor: hasModeles ? 'pointer' : 'default' }}>
                  <td className="svo-caret">{hasModeles ? (isOpen ? '▾' : '▸') : ''}</td>
                  <td className="rvn-marque-cell">{r.label} {hasModeles && <span className="svo-nb-modeles">({r.modeles.length})</span>}</td>
                  {has_before && <td className="rvn-num svo-arr-before">{cell(r.before)}</td>}
                  {months.map(m => <td key={m} className="rvn-num">{cell(r.par_mois[m])}</td>)}
                  <td className="rvn-num svo-arr-total">{fmtNumber(r.total)}</td>
                </tr>
                {isOpen && (r.modeles || []).map((m, j) => (
                  <tr key={`${i}-${j}`} className="svo-modele-row">
                    <td></td>
                    <td className="svo-modele-label">{m.label}</td>
                    {has_before && <td className="rvn-num svo-arr-before">{cell(m.before)}</td>}
                    {months.map(mm => <td key={mm} className="rvn-num">{cell(m.par_mois[mm])}</td>)}
                    <td className="rvn-num svo-arr-total">{fmtNumber(m.total)}</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="svo-arr-foot">
            <td></td>
            <td className="rvn-marque-cell"><strong>Total</strong></td>
            {has_before && <td className="rvn-num svo-arr-before"><strong>{fmtNumber(totaux.before)}</strong></td>}
            {months.map(m => <td key={m} className="rvn-num"><strong>{totaux.par_mois[m] ? fmtNumber(totaux.par_mois[m]) : '·'}</strong></td>)}
            <td className="rvn-num svo-arr-total"><strong>{fmtNumber(totaux.total)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Tableau châssis détaillé (triable + recherche). 1 ligne / véhicule en stock.
function ChassisTable({ rows }) {
  const [sortKey, setSortKey] = useState('age_mois');
  const [sortDir, setSortDir] = useState('desc');
  const [q, setQ] = useState('');

  const cols = [
    { key: 'marque',  label: 'Marque',          align: 'left',  fmt: (v) => v || '—' },
    { key: 'modele',  label: 'Modèle',          align: 'left',  fmt: (v) => v || '—' },
    { key: 'age_mois', label: 'Âge (mois)',     align: 'right', fmt: (v) => v == null ? '—' : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(v) },
    { key: 'immat',   label: 'Immat',           align: 'left',  fmt: (v) => v || '—' },
    { key: 'mec',     label: 'Date MEC',        align: 'left',  fmt: fmtDate },
    { key: 'entree',  label: 'Entrée stock',    align: 'left',  fmt: fmtDate },
    { key: 'km',      label: 'Km réel',         align: 'right', fmt: (v) => v == null ? '—' : new Intl.NumberFormat('fr-FR').format(v) },
  ];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let arr = rows || [];
    if (needle) {
      arr = arr.filter(r =>
        [r.marque, r.modele, r.immat, r.chassis, r.etablissement]
          .some(v => (v || '').toString().toLowerCase().includes(needle)));
    }
    arr = [...arr].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'string' || typeof bv === 'string')
        return sortDir === 'asc' ? (av || '').localeCompare(bv || '') : (bv || '').localeCompare(av || '');
      return sortDir === 'asc' ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });
    return arr;
  }, [rows, q, sortKey, sortDir]);

  const onSort = (key) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(['marque', 'modele', 'immat', 'mec', 'entree'].includes(key) ? 'asc' : 'desc'); }
  };

  return (
    <>
      <div className="svo-chassis-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher (marque, modèle, immat, chassis…)"
               value={q} onChange={e => setQ(e.target.value)} />
        <span className="svo-chassis-count">{filtered.length} véhicule{filtered.length > 1 ? 's' : ''}</span>
      </div>
      <div className="rvn-table-wrap svo-chassis-wrap">
        <table className="rvn-table">
          <thead>
            <tr>
              {cols.map(c => (
                <th key={c.key}
                    className={`${c.align === 'right' ? 'rvn-num' : ''} rvn-sortable ${sortKey === c.key ? 'rvn-sorted' : ''}`}
                    onClick={() => onSort(c.key)}>
                  {c.label}{sortKey === c.key && <span className="rvn-sort-arrow">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={i} className={r.dormant ? 'svo-row-dormant' : ''}>
                {cols.map(c => (
                  <td key={c.key} className={c.align === 'right' ? 'rvn-num' : ''}>{c.fmt(r[c.key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// Bloc unique « Détail » à onglets : Par établissement / Par marque / Stock
// par mois d'arrivage (même pattern .mar-monthly-toggle que VN/APV/Marché).
function StockDetailTabs({ data }) {
  const hasArrivages = data.arrivages && (data.arrivages.rows || []).length > 0;
  const [active, setActive] = useState('etablissement');
  const nbEtab = (data.par_etablissement || []).length;
  const nbMarque = (data.par_marque || []).length;
  const countSub =
    active === 'marque'   ? `${nbMarque} marque${nbMarque > 1 ? 's' : ''} · cliquer pour dérouler les modèles`
    : active === 'arrivage' ? "nb de châssis entrés en stock (12 derniers mois) · cliquer une marque pour dérouler ses modèles"
    : `${nbEtab} entité${nbEtab > 1 ? 's' : ''}`;

  return (
    <div className="rpa-dim-block">
      <div className="rpa-block-header">
        <h3>Détail</h3>
        <span className="rpa-block-sub">{countSub}</span>
      </div>
      <div className="mar-monthly-toggle">
        <button className={active === 'etablissement' ? 'active' : ''} onClick={() => setActive('etablissement')}>Par établissement</button>
        <button className={active === 'marque' ? 'active' : ''} onClick={() => setActive('marque')}>Par marque</button>
        {hasArrivages && (
          <button className={active === 'arrivage' ? 'active' : ''} onClick={() => setActive('arrivage')}>Stock par mois d'arrivage</button>
        )}
      </div>
      {active === 'etablissement' && <StockDimTable rows={data.par_etablissement} entityLabel="Établissement" />}
      {active === 'marque' && <MarqueModeleTable rows={data.par_marque} />}
      {active === 'arrivage' && hasArrivages && <ArrivagesPivot arrivages={data.arrivages} />}
    </div>
  );
}

function ReportingStockVO({ concessions, type = 'vo' }) {
  const isVn = type === 'vn';
  const title = isVn ? 'Stock VN' : 'Stock VO';
  const getDimensions = isVn ? reportingFinancierApi.getStockVnDimensions : reportingFinancierApi.getStockVoDimensions;
  const getKpis = isVn ? reportingFinancierApi.getStockVnKpis : reportingFinancierApi.getStockVoKpis;

  const effConcessions = useMemo(() => concessions || [], [concessions]);
  const [marque, setMarque]   = useState('');
  const [marques, setMarques] = useState([]);

  useEffect(() => {
    getDimensions()
      .then(r => setMarques(r.data?.marques || []))
      .catch(() => {});
  }, [getDimensions]);

  const { data, loading, error } = useReportingData(
    (signal) => getKpis(
      { ...(effConcessions.length ? { concession: effConcessions } : {}), ...(marque ? { marque } : {}) },
      signal,
    ).then(r => r.data),
    [type, JSON.stringify(effConcessions), marque],
  );

  const g = data?.kpis_globaux || {};

  return (
    <div className="rpa-dashboard rf-surface">
      <UpdatingOverlay show={loading} />
      <div className="rpa-dashboard-header">
        <div className="rpa-dashboard-title">
          <span className="rpa-dashboard-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 7l9-4 9 4-9 4-9-4z"/><path d="M3 7v10l9 4 9-4V7"/><path d="M12 11v10"/>
            </svg>
          </span>
          <div>
            <h2>{title}</h2>
            <div className="rpa-dashboard-subtitle">
              {data?.snapshot_date ? <>Stock au {fmtDate(data.snapshot_date)}</> : 'Stock à date'}
              {' · '}{fmtNumber(g.total)} véhicule{g.total > 1 ? 's' : ''}
              {' · '}<em>la période n'est pas utilisée (photo à l'instant T)</em>
            </div>
          </div>
        </div>
        <div className="rpa-toolbar">
          <select value={marque} onChange={e => setMarque(e.target.value)} className={`page-select ${marque ? 'active-filter' : ''}`}>
            <option value="">Toutes marques</option>
            {marques.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      {error && !data && <div className="rpa-error">⚠️ {error}</div>}

      {!data ? (
        <div className="rpa-loading">Chargement…</div>
      ) : (
        <>
          {/* KPI hero cards */}
          <div className="rpa-hero-grid">
            <KpiHero label="Véhicules en stock" value={fmtNumber(g.total)} sublabel={`${fmtNumber(g.nb_modeles)} modèles · ${fmtNumber(g.nb_marques)} marques`} />
            <KpiHero label="Âge moyen du stock" value={fmtMonths(g.age_moyen_mois)} sublabel={`${fmtNumber(g.age_moyen_jours)} jours`} />
            <KpiHero label="Stock dormant >120j" value={fmtNumber(g.dormant_nb)} sublabel={`${fmtPct(g.dormant_pct)} du stock`} alert />
            <KpiHero label="Valeur du stock" value={fmtMoney(g.valeur_stock)} sublabel="prix d'achat immobilisé" />
          </div>

          {/* Encart stock dormant */}
          {g.dormant_nb > 0 && (
            <div className="svo-dormant-banner">
              <span className="svo-dormant-emoji">⏳</span>
              <div>
                <strong>{fmtNumber(g.dormant_nb)} véhicules en stock depuis plus de 120 jours</strong>
                <span> — soit <strong>{fmtPct(g.dormant_pct)}</strong> du stock. À cibler en priorité (déstockage / actions commerciales).</span>
              </div>
            </div>
          )}

          {/* Par ancienneté */}
          <div className="rpa-dim-block">
            <div className="rpa-block-header"><h3>Répartition par ancienneté en stock</h3></div>
            <AncienneteChart rows={data.par_anciennete} />
          </div>

          {/* Bloc « Détail » à onglets : Par établissement / Par marque / Stock par mois d'arrivage */}
          <StockDetailTabs data={data} />

          {/* Détail châssis */}
          <div className="rpa-dim-block">
            <div className="rpa-block-header">
              <h3>Détail des châssis en stock</h3>
              <span className="rpa-block-sub">tri par défaut : du plus ancien en stock</span>
            </div>
            <ChassisTable rows={data.chassis} />
          </div>
        </>
      )}
    </div>
  );
}

export default ReportingStockVO;
